/**
 * liveStreamSupport.js — capability detection for the native-live video pipeline.
 *
 * The live pipeline streams ELV1-enveloped H.264/H.265 access units over a
 * WebSocket. Decoding/playback in the browser requires one of:
 *   1. WebCodecs `VideoDecoder` — preferred. iOS/iPadOS 16.4+, modern desktop
 *      browsers. Handled by NativeWebCodecsPlayer.
 *   2. Classic MediaSource Extensions (via JMuxer transmux -> <video>) — works
 *      on desktop + iPad Safari BELOW 16.4. NOT iPhone Safari (Apple ships only
 *      ManagedMediaSource on iPhone from 17.1+, by which point WebCodecs already
 *      exists, so those phones use tier 1). Handled by NativeLivePlayer.
 *
 * Devices with NEITHER (older iPhones < 16.4) cannot play this proprietary WS
 * stream client-side without a server-side HLS packager (not currently wired),
 * so the UI shows a clear "live view not supported" notice instead of a broken
 * perpetual spinner — and avoids opening a doomed WebSocket on metered data.
 *
 * All checks are evaluated at call-time (not import-time) and accept an optional
 * injected global so they can be unit-tested under Node.
 */

const resolveWindow = (win) => win || (typeof window !== 'undefined' ? window : undefined);

export const supportsWebCodecsVideoDecoder = (win) => {
  const w = resolveWindow(win);
  return !!w && typeof w.VideoDecoder === 'function';
};

// JMuxer feeds fragmented MP4 through the CLASSIC MediaSource API.
// ManagedMediaSource (iPhone 17.1+) is intentionally NOT treated as a fallback
// target here because those devices already expose WebCodecs (tier 1).
export const supportsJmuxerMse = (win) => {
  const w = resolveWindow(win);
  return !!w && typeof w.MediaSource === 'function';
};

export const supportsNativeHls = (doc) => {
  const d = doc || (typeof document !== 'undefined' ? document : undefined);
  if (!d || typeof d.createElement !== 'function') return false;
  try {
    const video = d.createElement('video');
    return typeof video.canPlayType === 'function'
      && video.canPlayType('application/vnd.apple.mpegurl') !== '';
  } catch (_) {
    return false;
  }
};

export const LIVE_PLAYER_WEBCODECS = 'webcodecs';
export const LIVE_PLAYER_MSE = 'mse';
export const LIVE_PLAYER_UNSUPPORTED = 'unsupported';

/**
 * Pick the best available live player for the current browser/device.
 * @returns {'webcodecs' | 'mse' | 'unsupported'}
 */
export const chooseLivePlayerMode = (win) => {
  if (supportsWebCodecsVideoDecoder(win)) return LIVE_PLAYER_WEBCODECS;
  if (supportsJmuxerMse(win)) return LIVE_PLAYER_MSE;
  return LIVE_PLAYER_UNSUPPORTED;
};
